/**
 * VoiceMinutesCounter — pill-shaped widget in the top nav showing how many
 * minutes of voice the current user has remaining for the month, colour-coded
 * green / amber / red as they get closer to their tier limit.
 *
 * Tier → minutes mapping is intentionally local to this component: the
 * billing surface owns the canonical entitlement, this is a UI mirror.
 */
import React from 'react';
import { Mic } from 'lucide-react';

const TIER_MINUTES = {
  // Phase 1 names (current backend PLANS display names)
  'First Steps':      0,
  'Steady':          15,
  'Together':       120,
  'Brave':          240,
  // Canonical uppercase display names (backend plans.py)
  'FIRST STEPS':      0,
  'STEADY':          15,
  'TOGETHER':       120,
  'BRAVE':          240,
  // V3 names
  'ENTRY':            0,
  'CHALLENGER':      15,
  'THE ELITE':      120,
  'MASTER':         240,
  // Legacy
  'Free Tier':        0,
  'Ava Experience':  30,
  'HiBFF Starter':   15,
  'AvA Prime':      120,
  'Ava Prime':      120,
  'HiBFF Plus':     120,
  'AvA Elite':      240,
  'Ava Elite':      240,
  'HiBFF Elite':    240,
};

export const VoiceMinutesCounter = ({ user }) => {
  const rawTierName = user?.subscription_tier_name || 'FIRST STEPS';
  // "TOGETHER (Gifted)" / "TOGETHER (Trial)" map to their base plan minutes.
  const tierName = rawTierName.replace(/\s*\((Gifted|Trial)\)\s*$/i, '');
  const totalMinutes = TIER_MINUTES[tierName] ?? 0;
  const usedMinutes  = user?.voice_minutes_used || 0;
  const remaining    = Math.max(0, totalMinutes - usedMinutes);
  const percentage   = totalMinutes > 0 ? (remaining / totalMinutes) * 100 : 0;

  const textColor = percentage > 50 ? 'text-emerald-600'
                  : percentage > 20 ? 'text-amber-600'
                  : 'text-red-600';
  const barColor  = percentage > 50 ? 'bg-emerald-500'
                  : percentage > 20 ? 'bg-amber-500'
                  : 'bg-red-500';

  return (
    <div
      className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full"
      data-testid="voice-minutes-counter"
    >
      <Mic size={15} className={textColor} />
      <div className="flex items-center gap-1.5">
        <span className={`text-sm font-bold ${textColor}`}>{remaining}</span>
        <span className="text-xs text-gray-400">/ {totalMinutes} min</span>
      </div>
      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};
